import React, { useEffect, useRef } from "react";

import { faSync } from "@fortawesome/free-solid-svg-icons";
import { faRotateReverse } from "@fortawesome/pro-duotone-svg-icons";
import { faFunnelDollar } from "@fortawesome/pro-solid-svg-icons";
import { Box, Tooltip } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";

import { BillingDocumentAction } from "actions";

import { IntegrationCredentialAction } from "actions/integrationCredentials";

import AgGridTable from "components/AgGrid/AgGridContainer";
import Button from "components/Button";
import { ButtonIcon } from "components/Button/FontAwesomeButton";
import { Column, Row } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import { useGetDownloadOutstandingSettlementReport } from "components/MoneyTable/outstandingSettlementReport";
import { TitleText } from "components/Text";
import { TimeSince } from "components/TimeSince";

import { AgGridTables } from "constants/aggrid";
import { IntegrationTypes } from "constants/integrations";
import { ApiModel } from "constants/loading";
import { ModalTypes } from "constants/navigation";
import { DeploymentPermissions } from "constants/permissions";

import { openModalLink } from "lib/navigation";

import {
  getActiveLivestockAgentDeployment,
  getActiveRole,
  getBillingDocuments,
  getIntegrationCredentialById,
  getIntegrationCredentialIdsByType,
  getProperties,
  selectBillingDocumentsAggridDataById,
} from "selectors";

import { useHasDeploymentPermission } from "hooks";
import { useLoadAllSales } from "hooks/useLoadAllSales";

import { MoneyColumnDefinitions } from "./columnDefinitions";

const getRowId = params => params.data.billingDocument.id;
const rowSelectionId = "billingDocument.id";

const detailCellRendererParams = {
  detailGridOptions: {
    columnDefs: [
      { field: "sentTime" },
      { field: "subject" },
      { field: "status" },
      { field: "formattedRecipient" },
    ],
    defaultColDef: {
      sortable: true,
      flex: 1,
    },
  },
  getDetailRowData: params => {
    params.successCallback(params.data.emails);
  },
};

export const MoneyTable = () => {
  const propertyByIdLookup = useSelector(getProperties);
  const userRoleSlug = useSelector(state => getActiveRole(state).slug);

  const context = {
    propertyByIdLookup,
    userRoleSlug,
  };
  const rowData = Object.values(
    useSelector(selectBillingDocumentsAggridDataById),
  );
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(BillingDocumentAction.request());
  }, [dispatch]);

  useLoadAllSales();

  const xeroCredentialIds = useSelector(
    getIntegrationCredentialIdsByType(IntegrationTypes.Xero),
  );

  const xeroCredentialId = xeroCredentialIds?.[0];

  const integrationCredential = useSelector(
    getIntegrationCredentialById(xeroCredentialId),
  );

  const lastSync = integrationCredential ? (
    <p>
      Integration synced{" "}
      {integrationCredential.integrationDocumentsLastSyncedAt
        ? TimeSince({
            utcDateTimeStr:
              integrationCredential.integrationDocumentsLastSyncedAt,
          })
        : "never"}
    </p>
  ) : null;

  const syncXeroDocuments = () =>
    dispatch(IntegrationCredentialAction.syncDocuments(xeroCredentialId));

  const syncButton = integrationCredential ? (
    <Column padding={1}>
      <Tooltip title="Refresh and Sync Documents Statuses from Xero">
        <Button data-tour="syncDocuments" inline onClick={syncXeroDocuments}>
          <Row padding={1}>
            <ButtonIcon icon={faSync} />
            <div>&nbsp;Sync Now</div>
          </Row>
        </Button>
      </Tooltip>
    </Column>
  ) : null;

  const forceNotLoading = useSelector(state => {
    const billingRuns = getBillingDocuments(state);
    return Object.keys(billingRuns).length > 0;
  });

  const [
    onGridReadyExtra,
    handleDownloadOutstandingSettlementReport,
  ] = useGetDownloadOutstandingSettlementReport();

  const agGridRef = useRef();

  const generateInterestCharges = () => {
    openModalLink(ModalTypes.Interest);
  };

  function onGridReady(agGrid) {
    agGridRef.current = agGrid;
    onGridReadyExtra(agGrid);
  }

  const hasInterestFeature = useHasDeploymentPermission(
    DeploymentPermissions.featureInterest,
  );

  const activeDeployment = useSelector(getActiveLivestockAgentDeployment);
  const interestEnabled =
    hasInterestFeature && activeDeployment.deploymentSettings?.enableInterest;
  const additionalActions = [
    {
      title: "Download Outstanding Settlement Report",
      icon: faFunnelDollar,
      onClick: handleDownloadOutstandingSettlementReport,
      dataTour: "outstandingSettlementReport",
    },
    interestEnabled && {
      title: "Generate Interest Charges",
      icon: faRotateReverse,
      onClick: generateInterestCharges,
      dataTour: "generateInterestCharges",
    },
  ].filter(Boolean);

  return (
    <Column fullHeight>
      <Box p={2}>
        <TitleText>Money</TitleText>
      </Box>
      <WaitForSync
        requiredData={[
          ApiModel.BILLING_DOCUMENTS,
          ApiModel.INTEGRATION_CREDENTIALS,
          ApiModel.SALES,
        ]}
        forceNotLoading={forceNotLoading}
      >
        <AgGridTable
          rowSelectionId={rowSelectionId}
          columnDefs={MoneyColumnDefinitions(interestEnabled)}
          context={context}
          extraHeaderComponents={[lastSync, syncButton]}
          getRowId={getRowId}
          rowData={rowData}
          additionalActions={additionalActions}
          onGridReady={onGridReady}
          tableName={AgGridTables.MONEY}
          detailCellRendererParams={detailCellRendererParams}
          masterDetail
        />
      </WaitForSync>
    </Column>
  );
};
